/**
 * Shared AudioSystem for all Pacman game versions
 *
 * Features:
 * - Dynamic background music switching
 * - Superpower mode music support
 * - Configurable audio files
 * - Volume control
 * - Error handling
 * - Event-driven audio triggers
 */

import { System } from '../core/System';
import { ResourceManager } from '../../resources/ResourceManager';
import { EventManager } from '../../events/EventManager';
import { Event } from '../../events/Event';
import { EventListener } from '../../events/EventListener';
import { Logger } from '../../utils/Logger';

export interface AudioConfig {
  // Background music files
  normalBackgroundMusic?: string;
  superpowerBackgroundMusic?: string;

  // Sound effect files
  dotCollectSound?: string;
  powerPelletSound?: string;
  ghostHitSound?: string;
  ghostEatenSound?: string;
  superModeGhostKillSound?: string;
  levelCompleteSound?: string;
  gameOverSound?: string;

  // Volume settings
  soundVolume?: number;
  musicVolume?: number;

  // Feature flags
  enableSuperpowerMusic?: boolean;
  enableSoundEffects?: boolean;
  enableBackgroundMusic?: boolean;
}

export class AudioSystem extends System implements EventListener {
  private resourceManager: ResourceManager;
  private eventManager: EventManager;
  protected logger: Logger;

  // Audio elements
  private backgroundMusic: HTMLAudioElement | null = null;
  private superpowerMusic: HTMLAudioElement | null = null;
  private musicPlaying: boolean = false;
  private isSuperpowerMode: boolean = false;

  // Volume settings
  private soundVolume: number = 0.6;
  private musicVolume: number = 0.4;

  // Audio state
  private audioEnabled: boolean = true;

  // Configurable audio files
  private config: AudioConfig;

  constructor(resourceManager: ResourceManager, config: AudioConfig = {}) {
    super();
    this.resourceManager = resourceManager;
    this.eventManager = EventManager.getInstance();
    this.logger = Logger.getInstance();

    // Set default configuration with all features enabled
    this.config = {
      // Default audio files
      normalBackgroundMusic: 'pacman_background.wav',
      superpowerBackgroundMusic: 'super_power_background.wav',
      dotCollectSound: 'dot_collect.wav',
      powerPelletSound: 'power_pellet.wav',
      ghostHitSound: 'ghost_hit.wav',
      ghostEatenSound: 'ghost_eaten.wav',
      superModeGhostKillSound: 'super_mode_ghost_kill.wav',
      levelCompleteSound: 'level_complete.wav',
      gameOverSound: 'game_over.wav',

      // Default volume settings
      soundVolume: 0.6,
      musicVolume: 0.4,

      // Default feature flags (all enabled)
      enableSuperpowerMusic: true,
      enableSoundEffects: true,
      enableBackgroundMusic: true,

      ...config, // Override with provided config
    };

    // Apply volume settings
    this.soundVolume = this.config.soundVolume!;
    this.musicVolume = this.config.musicVolume!;

    this.logger.info('AudioSystem: Initialized with shared audio system');

    // Preload all sound effects for immediate playback
    this.preloadSoundEffects();

    // Subscribe to game events
    this.setupEventListeners();
  }

  /**
   * Preload all sound effects for immediate playback
   */
  private preloadSoundEffects(): void {
    const soundEffects = [
      this.config.dotCollectSound!,
      this.config.powerPelletSound!,
      this.config.ghostHitSound!,
      this.config.ghostEatenSound!,
      this.config.superModeGhostKillSound!,
      this.config.levelCompleteSound!,
      this.config.gameOverSound!,
    ];

    this.logger.info('AudioSystem: Preloading sound effects...');

    soundEffects.forEach((soundFile) => {
      if (soundFile) {
        try {
          const audio = this.resourceManager.loadSound(soundFile);
          if (audio) {
            // Force preload by setting currentTime (triggers loading)
            audio.currentTime = 0;
            this.logger.debug(`AudioSystem: Preloaded sound ${soundFile}`);
          }
        } catch (error) {
          this.logger.warn(
            `AudioSystem: Failed to preload sound ${soundFile}: ${error}`
          );
        }
      }
    });

    this.logger.info('AudioSystem: Sound effects preloading complete');
  }

  /**
   * Setup event listeners for game audio triggers
   */
  private setupEventListeners(): void {
    // Always subscribe to basic events
    this.eventManager.subscribe('dot_collected', this);
    this.eventManager.subscribe('power_pellet_collected', this);
    this.eventManager.subscribe('ghost_eaten', this);
    this.eventManager.subscribe('pacman_died', this);
    this.eventManager.subscribe('level_complete', this);
    this.eventManager.subscribe('game_over', this);

    // Subscribe to superpower events only if enabled
    if (this.config.enableSuperpowerMusic) {
      this.eventManager.subscribe('super_mode_activated', this);
      this.eventManager.subscribe('super_mode_deactivated', this);
    }

    this.logger.info('AudioSystem: Event listeners setup complete');
  }

  /**
   * Update method called each frame
   */
  public update(deltaTime: number): void {
    // Start background music if not already playing and enabled
    if (
      this.audioEnabled &&
      !this.musicPlaying &&
      this.config.enableBackgroundMusic
    ) {
      this.startBackgroundMusic();
    }
  }

  /**
   * Handle game events and trigger appropriate sounds
   */
  public onEvent(event: Event): void {
    if (!this.audioEnabled) return;

    switch (event.getType()) {
      case 'dot_collected':
        if (this.config.enableSoundEffects) {
          this.playDotCollectSound();
        }
        break;

      case 'power_pellet_collected':
        if (this.config.enableSoundEffects) {
          this.playPowerPelletSound();
        }
        break;

      case 'ghost_eaten':
        if (this.config.enableSoundEffects) {
          // Check if this was a super mode ghost eating
          const isSuperModeKill = (event as any).isSuperModeKill;

          if (isSuperModeKill) {
            this.playSuperModeGhostKillSound();
          } else {
            this.playGhostHitSound();
          }
        }
        break;

      case 'pacman_died':
        if (this.config.enableSoundEffects) {
          this.playGhostHitSound(); // Same sound for pacman death
        }
        break;

      case 'level_complete':
        if (this.config.enableSoundEffects) {
          this.playLevelCompleteSound();
        }
        break;

      case 'game_over':
        if (this.config.enableSoundEffects) {
          this.playGameOverSound();
        }
        break;

      case 'super_mode_activated':
        if (this.config.enableSuperpowerMusic) {
          this.switchToSuperpowerMusic();
        }
        break;

      case 'super_mode_deactivated':
        if (this.config.enableSuperpowerMusic) {
          this.switchToNormalMusic();
        }
        break;
    }
  }

  /**
   * Play dot collection sound effect
   */
  private playDotCollectSound(): void {
    this.playSound(this.config.dotCollectSound!);
  }

  /**
   * Play power pellet collection sound effect
   */
  private playPowerPelletSound(): void {
    this.playSound(this.config.powerPelletSound!);
  }

  /**
   * Play ghost hit sound effect
   */
  private playGhostHitSound(): void {
    this.playSound(this.config.ghostHitSound!);
  }

  /**
   * Play ghost eaten sound effect
   */
  private playGhostEatenSound(): void {
    this.playSound(this.config.ghostEatenSound!);
  }

  /**
   * Play super mode ghost kill sound effect using Web Audio API for perfect sync
   */
  private playSuperModeGhostKillSound(): void {
    try {
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();

      // Create multiple oscillators for dramatic crash effect
      const frequencies = [150, 300, 450, 600, 750, 900, 1200, 1500]; // More frequencies for crash
      const oscillators: OscillatorNode[] = [];
      const gainNodes: GainNode[] = [];

      frequencies.forEach((freq, index) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(
          freq * 1.5, // Less dramatic frequency change
          audioContext.currentTime + 0.8 // Shorter duration
        );

        gainNode.gain.setValueAtTime(0.15, audioContext.currentTime); // Louder for crash
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + 0.8 // Shorter, more aggressive
        );

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.8); // Shorter duration

        oscillators.push(oscillator);
        gainNodes.push(gainNode);
      });

      // Add aggressive whoosh effect
      setTimeout(() => {
        const whooshOscillator = audioContext.createOscillator();
        const whooshGain = audioContext.createGain();

        whooshOscillator.connect(whooshGain);
        whooshGain.connect(audioContext.destination);

        whooshOscillator.frequency.setValueAtTime(
          80, // Lower frequency for more "crash" feel
          audioContext.currentTime
        );
        whooshOscillator.frequency.exponentialRampToValueAtTime(
          40,
          audioContext.currentTime + 0.5 // Shorter duration
        );

        whooshGain.gain.setValueAtTime(0.25, audioContext.currentTime); // Louder
        whooshGain.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + 0.5 // Shorter duration
        );

        whooshOscillator.start(audioContext.currentTime);
        whooshOscillator.stop(audioContext.currentTime + 0.5);
      }, 150); // Shorter delay for more immediate effect

      this.logger.debug('AudioSystem: Played super mode ghost kill sound');
    } catch (error) {
      this.logger.warn(
        `AudioSystem: Error playing super mode ghost kill sound: ${error}`
      );
    }
  }

  /**
   * Play level complete sound effect
   */
  private playLevelCompleteSound(): void {
    this.playSound(this.config.levelCompleteSound!);
  }

  /**
   * Play game over sound effect
   */
  private playGameOverSound(): void {
    try {
      const audio = this.resourceManager.loadSound(this.config.gameOverSound!);

      if (audio) {
        // Reset audio to beginning for immediate playback
        audio.currentTime = 0;

        // Set volume
        audio.volume = this.soundVolume;

        // Play sound and stop background music when it finishes
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Stop background music after game over sound starts playing
              this.stopBackgroundMusic();
              this.logger.info(
                'AudioSystem: Game over sound playing, background music stopped'
              );
            })
            .catch((error) => {
              this.logger.warn(
                `AudioSystem: Error playing game over sound: ${error}`
              );
            });
        }

        this.logger.debug(`AudioSystem: Played game over sound`);
      } else {
        this.logger.warn(`AudioSystem: Could not load game over sound file`);
      }
    } catch (error) {
      this.logger.warn(`AudioSystem: Error playing game over sound: ${error}`);
    }
  }

  /**
   * Generic sound playing method
   */
  private playSound(soundFile: string): void {
    try {
      const audio = this.resourceManager.loadSound(soundFile);

      if (audio) {
        // Reset audio to beginning for immediate playback
        audio.currentTime = 0;

        // Set volume
        audio.volume = this.soundVolume;

        // Play sound immediately (should be preloaded)
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            this.logger.warn(
              `AudioSystem: Error playing sound ${soundFile}: ${error}`
            );
          });
        }

        this.logger.debug(`AudioSystem: Played sound ${soundFile}`);
      } else {
        this.logger.warn(`AudioSystem: Could not load sound file ${soundFile}`);
      }
    } catch (error) {
      this.logger.warn(
        `AudioSystem: Error playing sound ${soundFile}: ${error}`
      );
    }
  }

  /**
   * Switch to superpower background music
   */
  public switchToSuperpowerMusic(): void {
    if (this.isSuperpowerMode) {
      this.logger.debug('AudioSystem: Already in superpower mode');
      return;
    }

    this.isSuperpowerMode = true;
    this.logger.info('AudioSystem: Switching to superpower music');

    // Stop current music
    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic.currentTime = 0;
    }

    // Load and play superpower music
    try {
      this.superpowerMusic = this.resourceManager.loadMusic(
        this.config.superpowerBackgroundMusic!
      );

      if (this.superpowerMusic) {
        this.superpowerMusic.volume = this.musicVolume;
        this.superpowerMusic.loop = true;
        this.backgroundMusic = this.superpowerMusic; // Use superpower music as current

        this.superpowerMusic
          .play()
          .then(() => {
            this.musicPlaying = true;
            this.logger.info(
              'AudioSystem: Started superpower background music'
            );
          })
          .catch((error) => {
            this.logger.warn(
              `AudioSystem: Error starting superpower background music: ${error}`
            );
          });
      } else {
        this.logger.warn(
          'AudioSystem: Could not load superpower background music file'
        );
      }
    } catch (error) {
      this.logger.warn(
        `AudioSystem: Error starting superpower background music: ${error}`
      );
    }
  }

  /**
   * Switch back to normal background music
   */
  public switchToNormalMusic(): void {
    if (!this.isSuperpowerMode) {
      this.logger.debug('AudioSystem: Already in normal mode');
      return;
    }

    this.isSuperpowerMode = false;
    this.logger.info('AudioSystem: Switching back to normal music');

    // Stop superpower music
    if (this.superpowerMusic) {
      this.superpowerMusic.pause();
      this.superpowerMusic.currentTime = 0;
    }

    // Reset music playing state and start normal background music
    this.musicPlaying = false;
    this.backgroundMusic = null; // Reset to force new load
    this.startBackgroundMusic();
  }

  /**
   * Start background music playback
   */
  private startBackgroundMusic(): void {
    if (this.musicPlaying) {
      this.logger.debug('AudioSystem: Background music already playing');
      return;
    }

    try {
      this.backgroundMusic = this.resourceManager.loadMusic(
        this.config.normalBackgroundMusic!
      );

      if (this.backgroundMusic) {
        // Set volume
        this.backgroundMusic.volume = this.musicVolume;

        // Set to loop continuously
        this.backgroundMusic.loop = true;

        // Play music
        this.backgroundMusic
          .play()
          .then(() => {
            this.musicPlaying = true;
            this.logger.info('AudioSystem: Started background music');
          })
          .catch((error) => {
            this.logger.warn(
              `AudioSystem: Error starting background music: ${error}`
            );
          });
      } else {
        this.logger.warn('AudioSystem: Could not load background music file');
      }
    } catch (error) {
      this.logger.warn(
        `AudioSystem: Error starting background music: ${error}`
      );
    }
  }

  /**
   * Stop background music playback
   */
  private stopBackgroundMusic(): void {
    if (!this.musicPlaying) {
      this.logger.debug('AudioSystem: Background music not playing');
      return;
    }

    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic.currentTime = 0;
    }

    this.musicPlaying = false;
    this.logger.info('AudioSystem: Stopped background music');
  }

  /**
   * Toggle audio on/off
   */
  public toggleAudio(): void {
    this.audioEnabled = !this.audioEnabled;

    if (!this.audioEnabled) {
      this.stopBackgroundMusic();
    } else if (!this.musicPlaying) {
      this.startBackgroundMusic();
    }

    this.logger.info(
      `AudioSystem: Audio ${this.audioEnabled ? 'enabled' : 'disabled'}`
    );
  }

  /**
   * Set sound volume (0.0 to 1.0)
   */
  public setSoundVolume(volume: number): void {
    this.soundVolume = Math.max(0, Math.min(1, volume));
    this.logger.info(`AudioSystem: Sound volume set to ${this.soundVolume}`);
  }

  /**
   * Set music volume (0.0 to 1.0)
   */
  public setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));

    if (this.backgroundMusic) {
      this.backgroundMusic.volume = this.musicVolume;
    }

    this.logger.info(`AudioSystem: Music volume set to ${this.musicVolume}`);
  }

  /**
   * Get current audio state
   */
  public isAudioEnabled(): boolean {
    return this.audioEnabled;
  }

  /**
   * Get current sound volume
   */
  public getSoundVolume(): number {
    return this.soundVolume;
  }

  /**
   * Get current music volume
   */
  public getMusicVolume(): number {
    return this.musicVolume;
  }

  /**
   * Check if superpower mode is active
   */
  public isSuperpowerModeActive(): boolean {
    return this.isSuperpowerMode;
  }

  /**
   * Check if superpower music is enabled
   */
  public isSuperpowerMusicEnabled(): boolean {
    return this.config.enableSuperpowerMusic ?? true;
  }

  /**
   * Check if sound effects are enabled
   */
  public isSoundEffectsEnabled(): boolean {
    return this.config.enableSoundEffects ?? true;
  }

  /**
   * Check if background music is enabled
   */
  public isBackgroundMusicEnabled(): boolean {
    return this.config.enableBackgroundMusic ?? true;
  }

  /**
   * Get current audio configuration
   */
  public getAudioConfig(): AudioConfig {
    return { ...this.config };
  }

  /**
   * Clean up audio resources
   */
  public cleanup(): void {
    this.stopBackgroundMusic();

    // Also stop superpower music if playing
    if (this.superpowerMusic) {
      this.superpowerMusic.pause();
      this.superpowerMusic.currentTime = 0;
    }

    this.resourceManager.clearAudioCache();
    this.logger.info('AudioSystem: Cleaned up audio resources');
  }
}
